import {
  MidtermTransactionModalProps,
  ModalConfigType,
  ModalLabels,
  activeClaimStepConfig,
  stepValues,
  transactionNoteStepConfig,
} from './midterm-transaction-modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TransactionStatus,
  useUpsertMidtermTransaction,
} from '@converge/graphql';

import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';
import { CreateMidtermTransactionField } from '@converge/data';
import { CreateMidtermTransactionFieldsSchema } from './midterm-transaction.validation.schema';
import { MidtermTransactionModal } from './midterm-transaction-modal';
import { MidtermTransactionNote } from './midterm-transaction-note';
import { ModalStep } from '../insured-policies.detail';
import { NoActiveClaimsConfirmation } from './active-claims-modal';
import { WaiverChangeControlDetails } from './waiver-change-control-details';
import moment from 'moment';
import { useConvergeSnackBar } from '../../../../contexts';
import { useForm } from 'react-hook-form';
import { useModalStepper } from './midterm-transaction-modal.hooks';
import { yupResolver } from '@hookform/resolvers/yup';

export const MidtermTransactionModalForWaiverChangeControl = ({
  policyUuid,
  effectiveDate,
  maxDate,
  transactionType,
  onClose,
  showClaimMessage,
  hasOpenClaims = false,
  claimsLoading = false,
}: MidtermTransactionModalProps) => {
  const { notify } = useConvergeSnackBar();
  const [noActiveClaims, setNoActiveClaims] = useState(false);
  const isOpen = useMemo(() => policyUuid !== '', [policyUuid]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentFirstStep, setCurrentFirstStep] = useState<ModalStep>(
    ModalStep.CANCELLATION_DETAILS
  );

  useEffect(() => {
    const newFirstStep =
      !claimsLoading && hasOpenClaims
        ? ModalStep.ACTIVE_CLAIM_INFO
        : ModalStep.CANCELLATION_DETAILS;
    setCurrentFirstStep(newFirstStep);
  }, [claimsLoading, hasOpenClaims]);

  const formMethods = useForm<CreateMidtermTransactionFields>({
    resolver: yupResolver(CreateMidtermTransactionFieldsSchema),
    defaultValues: {},
  });

  const handleOnClose = useCallback(() => {
    formMethods.reset();
    onClose();
    setNoActiveClaims(false);
  }, [onClose, formMethods]);

  const { upsertMidtermTransaction, loading } = useUpsertMidtermTransaction();

  const onSubmitMidtermTransactionHandler = useCallback(
    async (fields: CreateMidtermTransactionFields) => {
      let midtermResponse, midtermResponseErrors;
      try {
        if (!fields.notes) return;

        const { data, errors } = await upsertMidtermTransaction({
          variables: {
            input: {
              type: transactionType,
              acquiringEntity: fields?.legalName,
              effectiveDate: moment(fields?.dateOfAcquisition)
                .utc()
                .format('YYYY-MM-DDTHH:mm:ssZ'),
              notes: fields?.notes,
              policyUuid: policyUuid,
              status: TransactionStatus.Completed,
            },
          },
        });
        midtermResponse = data;
        midtermResponseErrors = errors;
        console.log(midtermResponse, midtermResponseErrors);
        notify(`Midterm transaction created successfully`, {
          variant: 'success',
        });
        handleOnClose();
      } catch (cause) {
        if (cause instanceof ApolloError) {
          const errorMessage = cause.graphQLErrors[0].message;
          notify(
            `Failed to upsert midterm transaction: ${
              errorMessage || 'No data returned by the API'
            }`,
            {
              variant: 'error',
            }
          );
        }
        console.error(`Failed to upsert midterm transaction: ${cause}`);
      }
    },
    [
      notify,
      upsertMidtermTransaction,
      handleOnClose,
      policyUuid,
      transactionType,
    ]
  );

  const submitForm = formMethods.handleSubmit(
    onSubmitMidtermTransactionHandler
  );
  const modalConfig: ModalConfigType = {
    ...activeClaimStepConfig(
      <NoActiveClaimsConfirmation
        isChecked={noActiveClaims}
        setIsChecked={setNoActiveClaims}
        setIsDisabled={setIsDisabled}
      />,
      ModalStep.CANCELLATION_DETAILS
    ),
    [ModalStep.CANCELLATION_DETAILS]: {
      title: (
        <Box className="modal-title">Waiver of Change of Control Details</Box>
      ),
      content: (
        <WaiverChangeControlDetails
          setIsDisabled={setIsDisabled}
          effectiveDate={effectiveDate}
          maxDate={maxDate}
        />
      ),
      prevLabel: ModalLabels.BACK,
      nextLabel: ModalLabels.NEXT,
      nextStep: ModalStep.TRANSACTION_NOTE,
    },
    ...transactionNoteStepConfig(
      <MidtermTransactionNote setIsDisabled={setIsDisabled} />
    ),
  };

  const { currentStep, handleNextPage, handleBackPage } = useModalStepper({
    modalConfig,
    firstStep: currentFirstStep,
    onClose: handleOnClose,
    setIsDisabled,
    onFinalStepSubmit: submitForm,
    hasOpenClaims,
  });

  interface CreateMidtermTransactionFields {
    [CreateMidtermTransactionField.LegalName]: string | undefined;
    [CreateMidtermTransactionField.DateOfAcquisition]: string | undefined;
    [CreateMidtermTransactionField.notes]: string | undefined;
  }

  const { title, body, prevButtonLabel, nextButtonLabel } = stepValues(
    currentStep,
    modalConfig[currentStep]
  );

  return (
    <MidtermTransactionModal
      isOpen={isOpen}
      formMethods={formMethods}
      onClose={handleOnClose}
      title={title}
      showClaimMessage={showClaimMessage}
      body={body}
      prevButtonLabel={prevButtonLabel}
      nextButtonLabel={nextButtonLabel}
      onBack={handleBackPage}
      onNext={handleNextPage}
      isNextDisabled={isDisabled}
      isLoading={loading}
      claimsLoading={claimsLoading}
    />
  );
};
